// frontend/src/Pages/DeliveryPage.jsx
import React, { useState, useEffect } from "react";
import DeliveryForm from "./DeliveryForm";
import DeliveryDashboard from "./DeliveryDashboard";
import DriverDeliveryRoute from "./DriverDeliveryRoute";
import { apiClient } from "../apiClient";

export default function DeliveryPage() {
  const userRole = localStorage.getItem("userRole");
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username");

  const [deliveryPlans, setDeliveryPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: new Date().toISOString().split("T")[0],
    dateTo: new Date().toISOString().split("T")[0],
    driverId: ""
  });

  const fetchDeliveryPlans = async (customFilters = null) => {
    const activeFilters = customFilters || filters;
    
    try {
      setLoadingPlans(true);
      setError(null);
      
      let plansData = [];

      if (userRole === "admin" || userRole === "manager") {
        // Fetch all plans
        plansData = await apiClient.get("/delivery-plans/");
      } else if (userRole === "driver") {
        // Fetch only my plans
        plansData = await apiClient.get("/delivery-plans/me");
      }

      if (!Array.isArray(plansData)) {
        throw new Error("Invalid data format from server");
      }

      // Filter by date range and driver (for admin/manager)
      let filteredPlans = plansData.filter(plan => {
        const planDate = plan.date;
        
        // Date range filter
        if (activeFilters.dateFrom && planDate < activeFilters.dateFrom) return false;
        if (activeFilters.dateTo && planDate > activeFilters.dateTo) return false;
        
        // Driver filter (only for admin/manager)
        if ((userRole === "admin" || userRole === "manager") && activeFilters.driverId) {
          if (plan.assigned_user_id !== parseInt(activeFilters.driverId)) return false;
        }
        
        return true;
      });

      // Fetch items for each plan
      const plansWithDetails = await Promise.all(
        filteredPlans.map(async (plan) => {
          try {
            const items = await apiClient.get(`/delivery-items/?delivery_plan_id=${plan.id}`);
            return { ...plan, items };
          } catch (err) {
            // Removed console.error
            return { ...plan, items: [] };
          }
        })
      );

      setDeliveryPlans(plansWithDetails);
    } catch (err) {
      setError(err.message || "Failed to load delivery plans");
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    fetchDeliveryPlans();
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchDeliveryPlans(newFilters);
  };

  // Block production_staff from accessing this page
  if (userRole === "production_staff") {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold text-red-600">❌ Access Denied</h2>
        <p className="text-gray-600 mt-1">You don't have access to this page.</p>
      </div>
    );
  }

  // Driver view - show driver route page
  if (userRole === "driver") {
    return <DriverDeliveryRoute driverId={userId} driverName={username} />;
  }

  // Admin/Manager view
  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#2D2F5E] mb-2">🚚 Delivery Management</h2>
        <p className="text-sm text-[#2D2F5E] opacity-75">Create and track delivery plans</p>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-lg border border-red-300">
          ❌ {error}
        </div>
      )}

      {/* Show form for admin/manager */}
      {(userRole === "admin" || userRole === "manager") && (
        <div className="mb-6">
          <DeliveryForm onPlanCreated={() => fetchDeliveryPlans()} />
        </div>
      )}
      
      {/* Dashboard with filters */}
      <DeliveryDashboard 
        plans={deliveryPlans || []} 
        loading={loadingPlans}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
}